import { useEffect, useState } from "react";
import {
  AbsoluteCenter,
  Box,
  Divider,
  Flex,
  Heading,
  Image,
  Text,
  useToast,
} from "@chakra-ui/react";
import RegisterForm from "./partials/RegisterForm";
import RegisterVerification from "./partials/RegisterVerification";
import { textPrimary, textSecondary, white } from "../../../utils/colors";
import testupscale from "../../../assets/images/testupscale.png";
import styled from "styled-components";
import { motion } from "framer-motion";
import GoogleButton from "../../../components/buttons/GoogleButton";
import EmailButton from "../../../components/buttons/EmailButton";
import Agreement from "../../../components/partials/Agreement";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useDispatch, useSelector } from "react-redux";
import { useGoogleLogin } from "@react-oauth/google";
// import { jwtDecode } from "jwt-decode"; // To decode the Google JWT token
import { registerTenantGoogleAction } from "../../../actions/tenantRegisterActions";
import {
  TENANTREGISTER_REGISTER_GOOGLE_RESET,
  TENANTREGISTER_REGISTER_RESET,
  TENANTREGISTER_RESEND_VERIFICATION_EMAIL_RESET,
  TENANTREGISTER_VERIFY_EMAIL_BYCODE_RESET,
} from "../../../constants/tenantRegisterConstants";
import dentacasa from "../../../assets/icons/dentacasa.svg";
import ToastComponent from "../../../components/partials/ToastComponent";
import RegisterCreating from "./partials/RegisterCreating";
import { Link } from "react-router-dom";
import LanguageButtonMain from "../../../components/buttons/LanguageButtonMain";
import { Helmet } from "react-helmet";
// import { useNavigate } from "react-router-dom";
dayjs.extend(duration);

const RegisterMain = () => {
  const { t } = useTranslation();
  const toastComponent = ToastComponent();
  const toast = useToast();
  const dispatch = useDispatch();
  // const navigate = useNavigate();

  // LANGUAGE
  const storedLanguage = localStorage.getItem("language");

  // STATE
  const [currentStep, setCurrentStep] = useState(1);

  const [clinicName, setClinicName] = useState("");
  const [registrationEmail, setRegistrationEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");

  const [verificationCode, setVerificationCode] = useState("");
  const [countdown, setCountdown] = useState(dayjs.duration(600, "seconds"));

  const [isCreating, setIsCreating] = useState(false);

  // VIDEO
  const [videoLoaded, setVideoLoaded] = useState(false);

  // HANDLERS
  const handleBackStepRegisterForm = () => {
    dispatch({ type: TENANTREGISTER_REGISTER_RESET });
    dispatch({ type: TENANTREGISTER_VERIFY_EMAIL_BYCODE_RESET });
    dispatch({ type: TENANTREGISTER_RESEND_VERIFICATION_EMAIL_RESET });
    setClinicName("");
    setRegistrationEmail("");
    setPassword("");
    setRepeatPassword("");
    setCurrentStep(1);
    setCountdown(dayjs.duration(180, "seconds"));
  };

  // REDUX
  // const authLogin = useSelector((state) => state.authLogin);
  // const { userInfo } = authLogin;

  const tenantRegisterRegister = useSelector(
    (state) => state.tenantRegisterRegister
  );
  const {
    loading: loadingRegister,
    error: errorRegister,
    success: successRegister,
  } = tenantRegisterRegister;

  const tenantRegisterVerifyEmailByCode = useSelector(
    (state) => state.tenantRegisterVerifyEmailByCode
  );
  const {
    loading: loadingVerify,
    error: errorVerify,
    success: successVerify,
    token,
    tenantID,
    role,
  } = tenantRegisterVerifyEmailByCode;

  const tenantRegisterRegisterGoogle = useSelector(
    (state) => state.tenantRegisterRegisterGoogle
  );
  const {
    loading: loadingGoogle,
    error: errorGoogle,
    // success: successGoogle,
  } = tenantRegisterRegisterGoogle;

  // GOOGLE
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      // console.log("Google Token Response:", tokenResponse);

      // ✅ Use tokenResponse.access_token to fetch user info
      const userInfoResponse = await fetch(
        "https://www.googleapis.com/oauth2/v3/userinfo",
        {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        }
      );
      const userInfo = await userInfoResponse.json();

      // console.log("Google User Info:", userInfo);

      // Prepare Google Data
      const googleData = {
        email: userInfo.email,
        googleId: userInfo.sub, // Google unique ID
        name: userInfo.name,
        picture: userInfo.picture,
      };

      dispatch(registerTenantGoogleAction(googleData, storedLanguage));
    },
    onError: (error) => {
      console.error("Google Login Error:", error);
    },
  });

  // USE EFFECT
  useEffect(() => {
    return () => {
      setCurrentStep(1);
      dispatch({ type: TENANTREGISTER_REGISTER_RESET });
      dispatch({ type: TENANTREGISTER_VERIFY_EMAIL_BYCODE_RESET });
      dispatch({ type: TENANTREGISTER_RESEND_VERIFICATION_EMAIL_RESET });
      dispatch({ type: TENANTREGISTER_REGISTER_GOOGLE_RESET });
      setClinicName("");
      setRegistrationEmail("");
      setPassword("");
      setRepeatPassword("");
      setVerificationCode("");

      // setCountdown(dayjs.duration(600, "seconds"));
      setIsCreating(false);
    };
  }, []);

  useEffect(() => {
    if (successRegister) {
      setCurrentStep(3);

      const interval = setInterval(() => {
        setCountdown((prev) => {
          const newDuration = prev.subtract(1, "second");

          if (newDuration.asSeconds() <= 0) {
            clearInterval(interval);

            handleBackStepRegisterForm();
            setCurrentStep(1);

            // navigate("/");

            return dayjs.duration(0, "seconds");
          }
          return newDuration;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [successRegister]);

  useEffect(() => {
    if (errorRegister) {
      // console.log("errorRegister", errorRegister);

      if (errorRegister === "Verification is still pending.") {
        toast({
          title: t("toasts.registerErrorVerificationPending"),
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } else if (errorRegister === "Failed to send verification email") {
        toastComponent.showToast(
          t("toasts.registerErrorFailedToSendVerificationEmail"),
          "error",
          9000
        );
      } else if (errorRegister === "Tenant with this email already exists") {
        toast({
          title: t("toasts.registerErrorTenantExists"),
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } else {
        console.log("errorRegister", errorRegister);
      }

      dispatch({ type: TENANTREGISTER_REGISTER_RESET });
    }
  }, [errorRegister]);

  useEffect(() => {
    if (errorGoogle) {
      // console.log("errorGoogle", errorGoogle);

      if (errorGoogle === "Tenant with this email already exists") {
        toastComponent.showToast(
          t("toasts.registerGoogleErrorTenantExists"),
          "error",
          9000
        );
      } else if (
        errorGoogle ===
        "Email service is currently unavailable. Please try again in a few minutes."
      ) {
        toastComponent.showToast(
          t("toasts.registerGoogleErrorEmailServiceUnavailable"),
          "error",
          9000
        );
      } else {
        toastComponent.showToast(
          t("toasts.registerGoogleError"),
          "error",
          9000
        );
      }

      dispatch({ type: TENANTREGISTER_REGISTER_GOOGLE_RESET });
    }
  }, [errorGoogle]);

  useEffect(() => {
    if (successVerify && token) {
      // navigate("/");
      // dispatch({ type: TENANTREGISTER_VERIFY_EMAIL_BYCODE_RESET });
      setIsCreating(true);
      setCurrentStep(4);
    }
  }, [successVerify, token]);

  useEffect(() => {
    if (errorVerify) {
      // console.log("errorVerify", errorVerify);

      if (errorVerify === "Invalid verification code") {
        toastComponent.showToast(
          t("toasts.verifyEmailByCodeErrorIncorrectCode"),
          "error",
          9000
        );
      } else if (
        errorVerify ===
        "Email service is currently unavailable. Please try again in a few minutes."
      ) {
        toastComponent.showToast(
          t("toasts.registerGoogleErrorEmailServiceUnavailable"),
          "error",
          9000
        );
      } else if (errorVerify === "Tenant not found") {
        toastComponent.showToast(
          t("toasts.verifyEmailExpiredTokenNoTenant"),
          "error",
          9000
        );
      } else {
        toastComponent.showToast(
          t("toasts.emailAlreadyVerified"),
          "error",
          9000
        );
      }

      dispatch({ type: TENANTREGISTER_VERIFY_EMAIL_BYCODE_RESET });
    }
  }, [errorVerify]);

  // LOGS

  return (
    <>
      <Helmet>
        <title>{t("pageTitle.register")}</title>
        <meta name="description" content={t("pageDescription.register")} />
      </Helmet>

      <Flex
        w="100%"
        h="100%"
        minH="100vh"
        align="stretch"
        position="relative"
        justify="flex-start"
        bg={white}
        as={motion.div}
        exit={{ opacity: 0, x: 0, transition: { duration: 0.15 } }}
        initial={{ opacity: 0, x: 0, y: 0 }}
        animate={{
          opacity: 1,
          x: 0,
          y: 0,
          transition: { duration: 0.15 },
        }}
      >
        <Box
          position={["absolute", "fixed"]}
          top={["0rem", "2.5rem"]}
          left={["0rem", "0rem"]}
          // bg="green"
          p={["1.5rem 1.5rem", "0 3rem"]}
          w={["100%", "100%"]}
          zIndex={1000}
        >
          <Flex w="100%" align="center" justify="space-between">
            <Link to="/">
              <Box h={["20px", "22px"]} w="initial">
                <Image src={dentacasa} h="100%" w="initial" />
              </Box>
            </Link>
            <Flex align="center" justify="flex-end" gap="0rem">
              <LanguageButtonMain />
            </Flex>
          </Flex>
        </Box>
        {/* LEFT */}
        <Box
          w={["100%", "29%"]}
          position={["fixed", "relative", "relative"]}
          flexShrink={0}
          display={["none", "block"]}
        >
          <Flex w="100%" h="100%" position="relative">
            <Image src={testupscale} w="100%" h="100%" objectFit="cover" />
            <Flex
              position="absolute"
              zIndex="1"
              top="0"
              left="0"
              w="100%"
              h="100%"
              as={motion.div}
              initial={{ opacity: 0, y: 0 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: { delay: 0, duration: 1 },
              }}
            >
              <Video
                playsInline
                autoPlay
                loop
                muted
                onLoadedData={() => setVideoLoaded(true)}
                onError={() => setVideoLoaded(false)}
                style={{ display: videoLoaded ? "block" : "none" }}
                src="https://bucket.mailersendapp.com/0p7kx4xmrvl9yjre/z3m5jgr1omx4dpyo/files/9ca7a800-8d36-414c-a9f1-d5ac05d4a695/videonew2.mp4"
              ></Video>
            </Flex>
          </Flex>
        </Box>

        {/* RIGHT */}
        <Flex
          w="100%"
          h={["100%", "100%"]}
          p={["12vh 1.5rem 3rem", "0px 0px 0px 100px"]}
          align={["flex-start", "flex-start"]}
          justify={["flex-start", "center"]}
          direction="column"
          maxH={["100%", "100vh"]}
          minH={["100%", "100vh"]}
        >
          {/* BACK BUTTON */}
          {/* <Box
            position="absolute"
            top="2rem"
            left={`calc(29% + 2rem)`}
            zIndex={1000}
          >
            <IconButton
              border={`1px solid ${newBorder}`}
              size="md"
              borderRadius="100rem"
              bg={white}
              fontSize="20px"
              icon={<Icon h="0.875rem" as={FaArrowLeft} />}
              // onClick={
              //   currentStep === 1
              //     ? () => window.history.back()
              //     : () => setCurrentStep(currentStep - 1)
              // }
              onClick={() => navigate("/")}
            />
          </Box> */}

          {/* MAIN */}

          <Box w="100%" maxW={["100%", "420px"]}>
            {currentStep === 1 && (
              <Box w="100%">
                <Heading
                  fontSize="1.875rem"
                  fontWeight="700"
                  color={textPrimary}
                  // mb={6}
                >
                  {t("common.registerHeading")}
                </Heading>

                <Text
                  color={textSecondary}
                  fontWeight="500"
                  fontSize={["1.125rem", "md"]}
                  m="0.5rem 0 1rem"
                >
                  {t("register.intro")}
                </Text>

                <GoogleButton
                  loading={loadingGoogle}
                  label={t("common.registerGoogle")}
                  onClick={() => googleLogin()}
                />

                <Box position="relative" py={3}>
                  <Divider />
                  <AbsoluteCenter bg="white" px="8">
                    <Text
                      textStyle="sm"
                      whiteSpace="nowrap"
                      color={textSecondary}
                    >
                      {t("common.or")}
                    </Text>
                  </AbsoluteCenter>
                </Box>

                <Box m="0 0 0.5rem">
                  <EmailButton
                    onClick={() => setCurrentStep(2)}
                    label={t("common.continueWithEmail")}
                  />
                </Box>

                <Agreement />
              </Box>
            )}

            {currentStep === 2 && (
              <RegisterForm
                language={storedLanguage}
                clinicName={clinicName}
                setClinicName={setClinicName}
                registrationEmail={registrationEmail}
                setRegistrationEmail={setRegistrationEmail}
                password={password}
                setPassword={setPassword}
                repeatPassword={repeatPassword}
                setRepeatPassword={setRepeatPassword}
                handleBack={handleBackStepRegisterForm}
                loadingRegister={loadingRegister}
              />
            )}
            {currentStep === 3 && (
              <RegisterVerification
                language={storedLanguage}
                registrationEmail={registrationEmail}
                verificationCode={verificationCode}
                setVerificationCode={setVerificationCode}
                setCurrentStep={setCurrentStep}
                countdown={countdown}
                setCountdown={setCountdown}
                handleBack={handleBackStepRegisterForm}
                loadingVerify={loadingVerify}
              />
            )}
            {currentStep === 4 && (
              <RegisterCreating
                token={token}
                tenantID={tenantID}
                role={role}
                isCreating={isCreating}
                setIsCreating={setIsCreating}
                handleBack={handleBackStepRegisterForm}
              />
            )}
          </Box>
        </Flex>
      </Flex>
    </>
  );
};

export default RegisterMain;

const Video = styled.video`
  display: block;
  width: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  height: 100%;
`;
